import type { AnimationGraph } from "@/document/model/animation";
import { animationDuration } from "@/document/animation/applyAnimation";
import type { EditorCommandHost } from "@/editor/commands/editorCommandHost";
import { documentStore, uiStore } from "@/editor/store";
import {
  addAnimateProperty,
  connectAnimation,
  connectSceneObjectToAnimation,
  connectAnimationTargetsToAnimation,
  createSceneObjectAnimateNode,
  createSceneObjectsGroupNode,
  createAnimationTargetsNode,
  duplicateSelectedAnimationNodes,
  insertAnimationNode,
  insertAnimationNodeAndConnect,
  moveAnimationNodes,
  selectAnimationConnections,
  selectAnimationNodes,
  selectObjectConnections,
  updateAnimationNode,
  updateAnimationVisualGroup,
  deleteAnimationVisualGroup,
} from "@/editor/commands";
import { bindCommand } from "@/editor/shell/shellCommandBinding";
import type { AnimationCanvasActions } from "./types";

export interface AnimationActionDeps {
  getAnimationGraph: () => AnimationGraph;
  getActiveAnimationRootId: () => string | undefined;
  onAnimationNodeArm?: AnimationCanvasActions["onAnimationNodeArm"];
  onAnimationPause?: AnimationCanvasActions["onAnimationPause"];
}

export function createAnimationActions(host: EditorCommandHost, deps: AnimationActionDeps): AnimationCanvasActions {
  return {
    onAnimationNodeInsert: bindCommand(host, insertAnimationNode),
    onAnimationNodeArm: deps.onAnimationNodeArm,
    onAnimationNodeSelectionChange: bindCommand(host, selectAnimationNodes),
    onAnimationConnectionSelectionChange: bindCommand(host, selectAnimationConnections),
    onAnimationObjectConnectionSelectionChange: keys => selectObjectConnections(keys),
    onAnimationNodesMove: bindCommand(host, moveAnimationNodes),
    onAnimationNodesDuplicate: bindCommand(host, duplicateSelectedAnimationNodes),
    onAnimationConnect: bindCommand(host, connectAnimation),
    onAnimationObjectConnect: bindCommand(host, connectSceneObjectToAnimation),
    onAnimationTargetsConnect: bindCommand(host, connectAnimationTargetsToAnimation),
    onAnimationObjectCreateNode: bindCommand(host, createSceneObjectAnimateNode),
    onAnimationTargetsCreateNode: bindCommand(host, createAnimationTargetsNode),
    onAnimationObjectsCreateGroupNode: bindCommand(host, createSceneObjectsGroupNode),
    onAnimationInsertAndConnect: bindCommand(host, insertAnimationNodeAndConnect),
    onAnimationNodeUpdate: bindCommand(host, updateAnimationNode),
    onAnimationVisualGroupUpdate: bindCommand(host, updateAnimationVisualGroup),
    onAnimationVisualGroupDelete: bindCommand(host, deleteAnimationVisualGroup),
    onAnimationAddAnimateProperty: bindCommand(host, addAnimateProperty),
    onAnimationPlay: rootNodeId => {
      if (rootNodeId !== deps.getActiveAnimationRootId()) {
        uiStore.patchAnimation({ loopRegion: undefined });
      }
      uiStore.patchAnimation({ activeRootId: rootNodeId });
      const duration = animationDuration(deps.getAnimationGraph(), rootNodeId, documentStore.getDocument());
      if (duration <= 0) {
        uiStore.setMessage("No animation motion");
        return;
      }
      uiStore.patchAnimation({ panelOpen: true });
      uiStore.patchAnimation({ playing: true });
      uiStore.patchAnimation({ timeMs: 0 });
    },
    onAnimationPause: deps.onAnimationPause,
  };
}
